import { ESCALA, PALETA_SERIES, deveRotular, formatarValor, gradienteVertical } from "../tema";
import { estiloRotulo, opcoesBase } from "./base";

/**
 * Família 11 — coluna empilhada.
 *
 * Composição de um total por categoria: exportação do complexo soja (grão,
 * farelo, óleo) por trimestre, abate por categoria animal. Cada série vira
 * um bloco da coluna; o total vai numa caixa acima.
 *
 * O eixo Y precisa ficar "visível" (só sem linha e sem rótulo): o
 * Highcharts desenha os totais pelo próprio eixo, e com visible:false eles
 * simplesmente não aparecem.
 */
const modelo = {
  id: "coluna-empilhada",
  label: "Coluna empilhada",
  descricao: "Partes que somam um total por categoria. Ex.: grão, farelo e óleo por trimestre.",

  build(spec) {
    const base = opcoesBase(spec);
    const totalCategorias = spec.dados?.categorias?.length || 0;
    const opcoesValor = spec.valores || {};
    const exibirEm = opcoesValor.exibirEm ?? "todos";
    const mostrarTotal = spec.extras?.mostrarTotal !== false;
    const percentual = spec.extras?.percentual === true;
    const rotulo = estiloRotulo(spec);

    return {
      ...base,
      chart: { ...base.chart, type: "column" },
      xAxis: { ...base.xAxis, categories: spec.dados?.categorias || [] },
      yAxis: {
        ...base.yAxis,
        visible: true,
        labels: { enabled: false },
        lineWidth: 0,
        gridLineWidth: 0,
        maxPadding: 0.12,
        ...(percentual ? { max: 100 } : {}),
        stackLabels: {
          enabled: mostrarTotal && !percentual,
          crop: false,
          overflow: "allow",
          y: -4 * ESCALA,
          backgroundColor: rotulo.backgroundColor,
          borderColor: rotulo.borderColor,
          borderWidth: rotulo.borderWidth,
          borderRadius: rotulo.borderRadius,
          padding: rotulo.padding,
          style: rotulo.style,
          formatter() {
            return formatarValor(this.total, opcoesValor);
          },
        },
      },
      plotOptions: {
        ...base.plotOptions,
        column: {
          stacking: percentual ? "percent" : "normal",
          borderWidth: 0,
          groupPadding: 0.08,
          pointPadding: 0.04,
          borderRadius: 0,
          dataLabels: {
            enabled: true,
            // Dentro do bloco, sem caixa: com caixa três valores empilhados
            // viram um bloco de etiquetas colado.
            inside: true,
            crop: true,
            style: {
              color: "#FFFFFF",
              fontSize: rotulo.style.fontSize,
              fontWeight: "bold",
              textOutline: `${1.5 * ESCALA}px rgba(0,0,0,0.7)`,
            },
            formatter() {
              // Bloco baixo demais para caber o número: omite.
              if ((this.point.shapeArgs?.height ?? 0) < 12 * ESCALA) return "";
              return percentual
                ? formatarValor(this.percentage, { decimais: 0, sufixo: "%" })
                : formatarValor(this.y, opcoesValor);
            },
          },
        },
      },
      series: (spec.dados?.series || []).map((serie, i) => {
        const padrao = PALETA_SERIES[i % PALETA_SERIES.length];
        return {
          name: serie.nome,
          color: gradienteVertical(serie.cor || padrao.cor, serie.corFim || padrao.corFim),
          data: (serie.valores || []).map((valor, j) => ({
            y: valor === null ? null : Number(valor),
            dataLabels: { enabled: deveRotular(j, totalCategorias, exibirEm) },
          })),
        };
      }),
    };
  },

  exemplo() {
    return {
      modelo: "coluna-empilhada",
      titulo: [
        { texto: "Complexo Soja", cor: "#F0F000" },
        { texto: " - Exportação ", cor: "#FFFFFF" },
        { texto: "(milhões de t)", cor: "#FFFFFF", tamanho: 8 },
      ],
      subtitulo: [{ texto: "Por trimestre - 2026", cor: "#4EA6FF" }],
      fonte: "Fonte: RURAL BUSINESS/Comex Stat",
      fundo: "#000000",
      fundoPlot: "nenhum",
      legenda: { visivel: true },
      dados: {
        categorias: ["1º tri", "2º tri", "3º tri"],
        series: [
          { nome: "Grão", cor: "#FFFF0B", corFim: "#7D8015", valores: [24.6, 42.8, 27.1] },
          { nome: "Farelo", cor: "#3DAA73", corFim: "#216443", valores: [5.1, 6.2, 6.4] },
          { nome: "Óleo", cor: "#FF8C00", corFim: "#7A3E00", valores: [0.4, 0.5, 0.3] },
        ],
      },
      valores: { decimais: 1, prefixo: "", sufixo: "", exibirEm: "todos" },
      rotulo: { corTexto: "#FFFFFF", corFundo: "#1A1A1A", corBorda: "#FFFFFF" },
      extras: { mostrarTotal: true, percentual: false },
    };
  },
};

export default modelo;
